import React, { useEffect } from "react";
import {
  Input,
  Button,
  FormFeedback,
  Col,
  Row,
  Card,
  CardBody,
  CardTitle,
  FormGroup,
} from "reactstrap";
import { useLocation, useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { yupResolver } from "@hookform/resolvers/yup";
import { NAME_MAXLENGTH } from "@constants/constants";
import Spinners from "@components/custom-table/Spinner";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
  addState,
  editState,
  getState,
  getStateCount,
} from "../services.js/state-services";
import { stateSchema } from "../../../helpers/validation-schema";
import { resetError, resetState } from "../controller/state-slice";
import { PHONE_NUMBER_MAXLENGTH } from "@constants/constants";
import CustomLabel from "../../../components/CustomLabel";
import { NAME_REGEX } from "../../../constants/constants";
import { handleInput } from "../../../helpers/helpers";
import Select from "react-select";
import { getCountryList } from "../../country/services/country-services";

export const AddState = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const route = pathname.split("/")[2];
  const stateId = pathname.split("/")[3];
  const success = useSelector((state) => state.state.success);
  const state = useSelector((state) => state.state.states);
  const countries = useSelector((state) => state.country.countries);
  const loading = useSelector((state) => state.state.loading);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const pageQuery = parseInt(searchParams.get("current_page"));
  const successMessage = useSelector((state) => state.state.successMessage);
  const errorMessage = useSelector((state) => state.state.errorMessage);

  const countryOptions = countries?.map((state) => ({
    value: state._id,
    label: state.name,
  }));

  const {
    control,
    handleSubmit,
    setError,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(stateSchema),
  });

  useEffect(() => {
    if (route === "add") {
      async function fetchCount() {
        const count = await getStateCount();
        setValue("sorting", count + 1);
      }
      fetchCount();
    }
  }, [setValue]);

  useEffect(() => {

    dispatch(getCountryList({ pageCount: 1, limit: 100 }));

  }, []);

  useEffect(() => {
    if (route !== "add") {
      dispatch(getState(stateId));
    }
  }, [stateId, dispatch, route]);

  useEffect(() => {
    if (success) {
      navigate(`/state`);
      dispatch(resetState());
    }
  }, [success, dispatch, navigate, successMessage, pageQuery]);

  useEffect(() => {
    if (errorMessage !== null) {
      toast.error(errorMessage?.message);

      errorMessage.data.items.forEach((item) => {
        const fieldName = Object.keys(item)[0];
        const message = item[fieldName];
        setError(fieldName, { type: "manual", message });
      });
    }
  }, [errorMessage, setError]);

  useEffect(() => {
    if (state) {

      setValue("name", state?.name);
      setValue("sorting", state?.sorting);
      setValue("country_id", state?.country_id);

    }
  }, [state, setValue]);

  const onSubmit = (data) => {
    if (route !== "edit") {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };
      dispatch(addState(formattedData));
    } else {
      const formattedData = {
        ...data,
        sorting: parseInt(data.sorting),
      };

      dispatch(editState({ id: stateId, data: formattedData }));
    }
  };
  const handleInputChange = (event, field) => {
    const { value } = event.target;
    const formattedValue = value.replace(/\D/g, "");
    setValue(field, formattedValue);
  };
  const customStyles = {
    control: (provided) => ({
      ...provided,
      borderColor: errors.country_id ? "red" : provided.borderColor,
      boxShadow: "none",
      "&:hover": {
        borderColor: errors.country_id ? "red" : provided.borderColor,
        boxShadow: "none",
      },
    }),
  };
  return (
    <div className="page-content">
      {loading ? (
        <Spinners />
      ) : (
        <>
          <ToastContainer autoClose={2000} />
          <Card>
            <CardBody className="text-start">
              <CardTitle className="mb-4 my-3">
                {route === "edit"
                  ? "Edit State"
                  : route === "view"
                  ? "View State"
                  : "Create State"}
              </CardTitle>

              <form onSubmit={handleSubmit(onSubmit)}>
                <Row>
                  <Col md={6}>
                    <FormGroup>
                    <CustomLabel htmlFor="Sorting" isRequired>
                        Sorting
                      </CustomLabel>
                      <Controller
                        name="sorting"
                        control={control}
                        defaultValue=""
                        render={({ field }) => (
                          <div>
                            <Input
                              {...field}
                              placeholder="Sorting"
                              invalid={!!errors.sorting}
                              maxLength={PHONE_NUMBER_MAXLENGTH}
                              onChange={(e) => handleInputChange(e, "sorting")}
                            />
                            {errors.sorting && (
                              <FormFeedback>
                                {errors.sorting.message}
                              </FormFeedback>
                            )}
                          </div>
                        )}
                      />
                    </FormGroup>
                  </Col>
                  <Col md={6}>
                    <FormGroup>
                      <CustomLabel htmlFor="Country" isRequired>
                      Country
                      </CustomLabel>
                      <Controller
                        name="country_id"
                        control={control}
                        render={({ field }) => (
                          <>
                            <Select
                              {...field}
                              options={countryOptions}
                              placeholder="Select Country"
                              onChange={(selectedOption) =>
                                field.onChange(
                                  selectedOption ? selectedOption.value : ""
                                )
                              }
                              value={countryOptions.find(
                                (option) => option.value === field.value
                              )}
                              styles={customStyles}
                              isClearable
                              menuPlacement="auto"
                              menuShouldScrollIntoView={true}
                              minMenuHeight={150}
                              maxMenuHeight={150}
                              isMenuScrollable={true}
                            />
                            {errors.country_id && (
                              <FormFeedback className="d-block">
                                {errors.country_id.message}
                              </FormFeedback>
                            )}
                          </>
                        )}
                      />
                    </FormGroup>
                  </Col>
         
                </Row>
                <Row>
                <Col md={6}>
                  <FormGroup>
                    <CustomLabel htmlFor="Name" isRequired>
                      State Name
                    </CustomLabel>
                    <Controller
                      name="name"
                      control={control}
                      defaultValue=""
                      render={({ field }) => (
                        <div>
                          <Input
                            {...field}
                            placeholder="Enter State Name"
                            invalid={!!errors.name}
                            onInput={(e) => handleInput(e, NAME_REGEX)}
                            maxLength={NAME_MAXLENGTH}
                          />
                          {errors.name && (
                            <FormFeedback>{errors.name.message}</FormFeedback>
                          )}
                        </div>
                      )}
                    />
                  </FormGroup>
                </Col>
                </Row>
                <Row>
                  <Col md={6}></Col>
                  <Col md={6}>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        type="button"
                        color="secondary"
                        onClick={() => {
                          navigate(`/state`);
                          dispatch(resetError());
                        }}
                      >
                        {route === "view" ? "Back" : "Cancel"}
                      </Button>
                      {route !== "view" ? (
                        <Button type="submit" color="primary">
                          {route === "edit" ? "Edit State" : "Create State"}
                        </Button>
                      ) : null}
                    </div>
                  </Col>
                </Row>
              </form>
            </CardBody>
          </Card>
        </>
      )}
    </div>
  );
};

export default AddState;
